import { AxiosRequestConfig } from 'axios';
import { treatmentApi } from 'src/api/treatment';
import {
  ClinicalWriteRequest,
  ClinicalWriteRoute,
  ClinicalWriteSeries,
  useClinicalWriteSeries,
} from 'src/sections/@dashboard/warehouse/hooks/useClinicalWriteSeries';
import { useStockWriteFeedback } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteFeedback';
import type { TreatmentFormSnapshot } from 'src/sections/@dashboard/profile/components/ProfileTreatments/treatmentFormValues';
import {
  AddProfileTreatmentDto,
  UpdateProfileTreatmentDto,
} from 'woofwoof-api';

/**
 * Every Facility treatment create and edit is a series of this kind, on
 * every screen, so a form reopened anywhere resends what another one froze.
 * Its own kind: a treatment and a medication never share a key.
 */
export const TREATMENT_WRITE = 'clinical-treatment';

/** The form's snapshot rides with the frozen request (R3-4, `frozenValues`). */
export type TreatmentCreateSeries = ClinicalWriteSeries<
  null,
  AddProfileTreatmentDto,
  TreatmentFormSnapshot
>;
export type TreatmentEditSeries = ClinicalWriteSeries<
  string,
  UpdateProfileTreatmentDto,
  TreatmentFormSnapshot
>;
type CreateRequest = ClinicalWriteRequest<
  null,
  AddProfileTreatmentDto,
  TreatmentFormSnapshot
>;
type EditRequest = ClinicalWriteRequest<
  string,
  UpdateProfileTreatmentDto,
  TreatmentFormSnapshot
>;

/*
 * The endpoint is read from the frozen route's `via` alone: the patient's
 * route (with the visit, if any, in the body), or the visit's own route,
 * where the server resolves the patient (none on a guest visit). The backend
 * hashes the route into the operation key, so a resend from another screen
 * must go exactly where the first attempt went.
 */
function sendCreate(
  { route, body }: CreateRequest,
  options: AxiosRequestConfig,
): Promise<unknown> {
  return route.via === 'patient'
    ? treatmentApi.treatmentRecordControllerAddProfileTreatment(
        {
          profileId: route.profileId,
          addProfileTreatmentDto: {
            ...body,
            visitId: route.visitId ?? undefined,
          },
        },
        options,
      )
    : treatmentApi.treatmentRecordControllerAddVisitTreatment(
        { visitId: route.visitId, addProfileTreatmentDto: body },
        options,
      );
}

function sendEdit(
  { route, target: treatmentId, body }: EditRequest,
  options: AxiosRequestConfig,
): Promise<unknown> {
  return route.via === 'patient'
    ? treatmentApi.treatmentRecordControllerUpdateProfileTreatment(
        {
          profileId: route.profileId,
          treatmentId,
          updateProfileTreatmentDto: {
            ...body,
            visitId: route.visitId ?? undefined,
          },
        },
        options,
      )
    : treatmentApi.treatmentRecordControllerUpdateVisitTreatment(
        {
          visitId: route.visitId,
          treatmentRecordId: treatmentId,
          updateProfileTreatmentDto: body,
        },
        options,
      );
}

interface TreatmentWriteOptions<TBody> {
  /**
   * The screen's patient, visit and endpoint (`via`); a create's series is
   * the patient's and visit's, whichever endpoint (see `ClinicalWriteRoute`).
   */
  route: ClinicalWriteRoute;
  /**
   * After the Warehouse refresh and shortage snackbars. `body` is what was
   * sent: the frozen body, reminder intent included (R4-3).
   */
  onSuccess: (response: unknown, body: TBody) => void;
  onError: (error: unknown) => void;
}

/**
 * R3-4: a Facility treatment create, with or without stock, as a clinical
 * write series keyed by its patient and visit (notes-2, Tasks 22/24), so
 * every screen of one patient and visit shares it.
 */
export function useTreatmentCreateSeries({
  route,
  onSuccess,
  onError,
}: TreatmentWriteOptions<AddProfileTreatmentDto>): TreatmentCreateSeries {
  const onStockWrite = useStockWriteFeedback();
  return useClinicalWriteSeries(sendCreate, {
    operation: TREATMENT_WRITE,
    target: null,
    route,
    onSuccess: (response, request) => {
      onStockWrite(response);
      onSuccess(response, request.body);
    },
    onError,
  });
}

/**
 * R3-4: a Facility treatment edit as a clinical write series of the entry
 * `editingId` (`''` while no treatment is open): `pending` is that entry's.
 */
export function useTreatmentEditSeries({
  route,
  editingId,
  onSuccess,
  onError,
}: TreatmentWriteOptions<UpdateProfileTreatmentDto> & {
  editingId: string;
}): TreatmentEditSeries {
  const onStockWrite = useStockWriteFeedback();
  return useClinicalWriteSeries(sendEdit, {
    operation: TREATMENT_WRITE,
    target: editingId,
    route,
    onSuccess: (response, request) => {
      onStockWrite(response);
      onSuccess(response, request.body);
    },
    onError,
  });
}
